import type {
  AuthorGetPayload,
  AuthorSelect,
  LifeDomainGetPayload,
  LifeDomainSelect,
  ResAuthorGetPayload,
  ResAuthorSelect,
  ResTaxonomyGetPayload,
  ResTaxonomySelect,
  ResourceItemGetPayload,
  ResourceItemSelect,
  ResourceLinkGetPayload,
  ResourceLinkSelect,
  TaxonomyGetPayload,
  TaxonomySearchLogGetPayload,
  TaxonomySearchLogSelect,
  TaxonomySelect,
} from '@erikdakoda/database/types';
import type { ResourcePlatformId } from '@erikdakoda/database/enums';

export type ResourcePlatform = {
  id: ResourcePlatformId;
  name: string;
  description: string;
  urlTemplate: string;
};

export const TaxonomyForGridSelect = {
  id: true,
  name: true,
  publishedAt: true,
  description: true,
  domain: true,
  type: true,
  tags: true,
  status: true,
  mapsTo: true,
  mapsFrom: true,
  _count: {
    select: {
      mapsTo: true,
      mapsFrom: true,
    },
  },
} satisfies TaxonomySelect;

export type TaxonomyForGrid = TaxonomyGetPayload<{
  select: typeof TaxonomyForGridSelect;
}>;

// Same shape as TaxonomyForGrid today; kept as its own named select/type so
// TaxonomySubGrid (nested under a resource item / mapping) can evolve its
// column set independently of the top-level TaxonomyGrid.
export const TaxonomySubForGridSelect = TaxonomyForGridSelect;

export type TaxonomySubForGrid = TaxonomyForGrid;

export const LifeDomainForGridSelect = {
  id: true,
  domainId: true,
  number: true,
  title: true,
  subtitle: true,
  description: true,
  tags: true,
  status: true,
  conditional: true,
  imageId: true,
  icon: true,
  _count: {
    select: {
      resourceItems: true,
    },
  },
} satisfies LifeDomainSelect;

export type LifeDomainForGrid = LifeDomainGetPayload<{
  select: typeof LifeDomainForGridSelect;
}>;

export const LifeDomainForSliderSelect = {
  domainId: true,
  number: true,
  title: true,
  subtitle: true,
  description: true,
  conditional: true,
  imageId: true,
} satisfies LifeDomainSelect;

export type LifeDomainForSlider = LifeDomainGetPayload<{
  select: typeof LifeDomainForSliderSelect;
}>;

export const ResTaxonomyForGridSelect = {
  id: true,
  similarity: true,
  taxonomyId: true,
  resourceItemId: true,
  status: true,
  publishedAt: true,
  createdAt: true,
  updatedAt: true,
} satisfies ResTaxonomySelect;

export type ResTaxonomyForGrid = ResTaxonomyGetPayload<{
  select: typeof ResTaxonomyForGridSelect;
}>;

export const ResAuthorForGridSelect = {
  id: true,
  authorId: true,
  resourceId: true,
  status: true,
  createdAt: true,
  updatedAt: true,
} satisfies ResAuthorSelect;

export type ResAuthorForGrid = ResAuthorGetPayload<{
  select: typeof ResAuthorForGridSelect;
}>;

export const ResourceLinkForGridSelect = {
  id: true,
  platform: true,
  format: true,
  dataUrl: true,
  url: true,
  externalId: true,
  description: true,
  durationSeconds: true,
  score: true,
  scoreCount: true,
  price: true,
  priceType: true,
  currency: true,
  resourceItemId: true,
  resourceItem: {
    select: {
      id: true,
      title: true,
      domain: true,
      status: true,
    },
  },
  status: true,
  publishedAt: true,
  createdAt: true,
  updatedAt: true,
} satisfies ResourceLinkSelect;

export type ResourceLinkForGrid = ResourceLinkGetPayload<{
  select: typeof ResourceLinkForGridSelect;
}>;

export const AuthorForGridSelect = {
  id: true,
  name: true,
  images: true,
  url: true,
  bio: true,
  summary: true,
  status: true,
  createdAt: true,
  updatedAt: true,
  _count: {
    select: {
      resAuthors: true,
    },
  },
} satisfies AuthorSelect;

export type AuthorForGrid = AuthorGetPayload<{
  select: typeof AuthorForGridSelect;
}>;

export const ResourceItemForGridSelect = {
  id: true,
  itemType: true,
  title: true,
  subtitle: true,
  description: true,
  summary: true,
  score: true,
  scoreCount: true,
  domain: true,
  coverImageId: true,
  categories: true,
  publishedOn: true,
  websiteUrl: true,
  status: true,
  createdAt: true,
  updatedAt: true,
  _count: {
    select: {
      resTaxonomies: true,
      resAuthors: true,
      resourceLinks: true,
    },
  },
} satisfies ResourceItemSelect;

export type ResourceItemForGrid = ResourceItemGetPayload<{
  select: typeof ResourceItemForGridSelect;
}>;

export const ResourceItemForDetailSelect = {
  id: true,
  createdAt: true,
  updatedAt: true,
  status: true,
  publishedAt: true,
  version: true,
  indexedAt: true,
  processingAt: true,
  itemType: true,
  slug: true,
  title: true,
  subtitle: true,
  description: true,
  summary: true,
  domain: true,
  language: true,
  score: true,
  scoreCount: true,
  coverImageId: true,
  category: true,
  categories: true,
  publishedOn: true,
  websiteUrl: true,
  copyright: true,
  sources: true,
  tags: true,
  resTaxonomies: {
    select: ResTaxonomyForGridSelect,
  },
  resAuthors: {
    select: ResAuthorForGridSelect,
  },
  resourceLinks: {
    select: ResourceLinkForGridSelect,
  },
} satisfies ResourceItemSelect;

export type ResourceItemForDetail = ResourceItemGetPayload<{
  select: typeof ResourceItemForDetailSelect;
}>;

// ResourceItemSubGrid nests ResourceItemDetailPanel in its detail panel, so it
// needs the same full payload as ResourceItemForDetail; kept as its own named
// select/type so the sub grid can diverge later without affecting the
// top-level ResourceItemGrid.
export const ResourceItemSubForGridSelect = ResourceItemForDetailSelect;

export type ResourceItemSubForGrid = ResourceItemForDetail;

export const TaxonomySearchLogForGridSelect = {
  id: true,
  createdAt: true,
  searchText: true,
  domains: true,
  types: true,
  limit: true,
  minSimilarity: true,
  resultCount: true,
  results: true,
  analyzedAt: true,
} satisfies TaxonomySearchLogSelect;

export type TaxonomySearchLogForGrid = TaxonomySearchLogGetPayload<{
  select: typeof TaxonomySearchLogForGridSelect;
}>;
